import { Virtuoso } from 'react-virtuoso'
import { InfiniteData } from '@tanstack/react-query'

import Loader from 'common/components/loaders/Loader'

import {
  CategoryProductsResponse,
  Product,
  ProductDetailsResponse,
} from '../../api/api'
import { Category } from '../../api/queries/useGetCategoryDetailsQuery'
import CategoryDetails from '../CategoryDetails'

import ProductItem from './ProductItem'

type Props = {
  products: InfiniteData<CategoryProductsResponse> | undefined
  isLoading: boolean
  isFetchingNextPage: boolean
  categoryDetails: Category | undefined
  productDetails: ProductDetailsResponse | undefined
  fetchNextPage: () => void
  hasNextPage: boolean | undefined
  onEditDescription: () => void
  onEditProductDescription: (product: Product) => void
  isProductSelected: boolean
}

const CategoryProductsDrawer = ({
  products,
  isLoading,
  isFetchingNextPage,
  categoryDetails,
  productDetails,
  fetchNextPage,
  hasNextPage,
  onEditDescription,
  onEditProductDescription,
  isProductSelected,
}: Props) => {
  const allProducts = products?.pages.flatMap((page) => page.data) || []
  const productCount = products?.pages[0]?.totalItems ?? 0

  const hasProducts = allProducts.length > 0 || !!productDetails

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1">
        <Loader className="m-4" />
      </div>
    )
  }

  return (
    <div className="mt-6 flex flex-col flex-1">
      <CategoryDetails
        categoryDetails={categoryDetails}
        onEditDescription={onEditDescription}
      />

      {hasProducts ? (
        <>
          <div className="flex justify-between items-end mb-4 mt-4">
            <h3 className="text-xs font-medium uppercase text-coolGray-800">
              List
            </h3>
            {!isProductSelected && (
              <span className="text-xxs text-coolGray-500">
                {productCount} {productCount === 1 ? 'Item' : 'Items'}
              </span>
            )}
          </div>
          <div className="flex-1 ">
            {isProductSelected && productDetails ? (
              <div role="listitem">
                <ProductItem
                  product={productDetails}
                  onEditDescription={() =>
                    onEditProductDescription(productDetails as Product)
                  }
                />
              </div>
            ) : (
              <Virtuoso
                data={allProducts}
                role="list"
                aria-label="List"
                totalCount={productCount}
                data-testid="category-products-list"
                overscan={1}
                itemContent={(_, product) => (
                  <div key={product.id} role="listitem">
                    <ProductItem
                      product={product}
                      onEditDescription={() =>
                        onEditProductDescription(product)
                      }
                    />
                    <div className="self-stretch px-4">
                      <div className="self-stretch h-px bg-gold-200" />
                    </div>
                  </div>
                )}
                endReached={() => {
                  if (hasNextPage && !isProductSelected) {
                    fetchNextPage()
                  }
                }}
                components={{
                  Footer: () =>
                    isFetchingNextPage && !isProductSelected ? (
                      <Loader className="m-4" data-testid="next-page-loader" />
                    ) : null,
                }}
              />
            )}
          </div>
        </>
      ) : (
        <div className="text-center p-6 text-coolGray-500">
          No items found in this category.
        </div>
      )}
    </div>
  )
}

export default CategoryProductsDrawer
